import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Mail, Calendar, Clock, Send, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface ReportSettings {
  id: string;
  report_frequency: "weekly" | "monthly";
  recipient_emails: string[];
  is_enabled: boolean;
  last_sent_at: string | null;
}

export function ReportSettings() {
  const [settings, setSettings] = useState<ReportSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const { data, error } = await supabase
      .from("support_report_settings")
      .select("*")
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error(error);
    } else if (data) {
      setSettings(data as ReportSettings);
    } else {
      // Create default settings if none exist
      const { data: newData, error: insertError } = await supabase
        .from("support_report_settings")
        .insert({
          report_frequency: "weekly",
          recipient_emails: [],
          is_enabled: false,
        })
        .select()
        .single();

      if (!insertError && newData) {
        setSettings(newData as ReportSettings);
      }
    }
    setLoading(false);
  };

  const updateSettings = async (updates: Partial<ReportSettings>) => {
    if (!settings) return;
    
    setSaving(true);
    const { error } = await supabase
      .from("support_report_settings")
      .update(updates)
      .eq("id", settings.id);

    if (error) {
      toast.error("Failed to update settings");
    } else {
      setSettings({ ...settings, ...updates });
      toast.success("Settings updated");
    }
    setSaving(false);
  };

  const addEmail = () => {
    if (!newEmail.trim() || !settings) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (settings.recipient_emails.includes(newEmail.trim())) {
      toast.error("Email already added");
      return;
    }

    const updatedEmails = [...settings.recipient_emails, newEmail.trim()];
    updateSettings({ recipient_emails: updatedEmails });
    setNewEmail("");
  };

  const removeEmail = (email: string) => {
    if (!settings) return;
    const updatedEmails = settings.recipient_emails.filter(e => e !== email);
    updateSettings({ recipient_emails: updatedEmails });
  };

  const sendTestReport = async () => {
    if (!settings || settings.recipient_emails.length === 0) {
      toast.error("Add at least one recipient email first");
      return;
    }

    setSendingTest(true);
    try {
      const { error } = await supabase.functions.invoke("send-support-report", {
        body: { test: true },
      });

      if (error) throw error;
      toast.success("Test report sent!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to send test report");
    }
    setSendingTest(false);
  };

  if (loading) {
    return <div className="p-4 text-muted-foreground">Loading settings...</div>;
  }

  if (!settings) {
    return <div className="p-4 text-destructive">Failed to load settings</div>;
  }

  return (
    <div className="space-y-6">
      {/* Enable/Disable */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Reports
          </CardTitle>
          <CardDescription>
            Receive automated reports summarizing support metrics and bot performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable Automated Reports</Label>
              <p className="text-sm text-muted-foreground">
                Reports will be sent based on your frequency setting
              </p>
            </div>
            <Switch
              checked={settings.is_enabled}
              onCheckedChange={(checked) => updateSettings({ is_enabled: checked })}
              disabled={saving}
            />
          </div>
        </CardContent>
      </Card>

      {/* Frequency */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Report Frequency
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={settings.report_frequency}
            onValueChange={(value: "weekly" | "monthly") => 
              updateSettings({ report_frequency: value })
            }
            disabled={saving}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly (Every Monday)</SelectItem>
              <SelectItem value="monthly">Monthly (1st of month)</SelectItem>
            </SelectContent>
          </Select>
          
          {settings.last_sent_at && (
            <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Last sent: {format(new Date(settings.last_sent_at), "PPp")}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Recipients */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Recipients
          </CardTitle>
          <CardDescription>
            Add email addresses to receive the automated reports
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="email@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addEmail()}
            />
            <Button onClick={addEmail} disabled={saving}>
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {settings.recipient_emails.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recipients added yet</p>
            ) : (
              settings.recipient_emails.map((email) => (
                <Badge key={email} variant="secondary" className="flex items-center gap-1">
                  {email}
                  <button
                    onClick={() => removeEmail(email)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Test Button */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Send Test Report</p>
              <p className="text-sm text-muted-foreground">
                Send a test report to verify your settings
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={sendTestReport}
              disabled={sendingTest || settings.recipient_emails.length === 0}
            >
              {sendingTest ? "Sending..." : "Send Test"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Contents Preview */}
      <Card>
        <CardHeader>
          <CardTitle>What's Included in Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <Badge variant="outline" className="w-2 h-2 p-0 rounded-full bg-primary" />
              Total conversations and resolution rates
            </li>
            <li className="flex items-center gap-2">
              <Badge variant="outline" className="w-2 h-2 p-0 rounded-full bg-primary" />
              AI self-serve rate (conversations resolved without human)
            </li>
            <li className="flex items-center gap-2">
              <Badge variant="outline" className="w-2 h-2 p-0 rounded-full bg-primary" />
              Average response time metrics
            </li>
            <li className="flex items-center gap-2">
              <Badge variant="outline" className="w-2 h-2 p-0 rounded-full bg-primary" />
              Conversation volume by tag/category
            </li>
            <li className="flex items-center gap-2">
              <Badge variant="outline" className="w-2 h-2 p-0 rounded-full bg-primary" />
              Custom Q&A training usage statistics
            </li>
            <li className="flex items-center gap-2">
              <Badge variant="outline" className="w-2 h-2 p-0 rounded-full bg-primary" />
              Trending support topics
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
